import { inject, Injectable } from '@angular/core';
import { EipConfig } from '@schir-int-client/api-root';
import { EipConnection, EipMessageListener } from './eip.connection';
import { EipContext } from './eip.context';
import { EipLawsuit } from './eip.lawsuit';
import { EipMessages } from './eip.messages';
import { EipStorage } from './eip.storage';
import { EipDigitalFile } from './eip.digital-file';
import { EipErrorhandler } from './eip.errorhandler';

@Injectable({ providedIn: 'root' })
export class EipRegistration implements EipMessageListener {
	private errorHandler = inject(EipErrorhandler);
	private context = inject(EipContext);
	public storage = inject(EipStorage);
	private connection = inject(EipConnection);
	private eipLawsuit = inject(EipLawsuit);
	private eipDigitalFile = inject(EipDigitalFile);

	private config: EipConfig;
	private started: boolean = false;

	private showViewRequestId: string;

	private readonly expectedViewIdentifier = 'de.justiz.eip.schiffsregister.ui.view.centralmask';

	constructor() {
		this.connection.addMessageListener(this);
	}

	public isConfigured(): boolean {
		return this.storage.isConfigured();
	}

	public isStarted(): boolean {
		return this.started;
	}

	public start(config: EipConfig) {
		this.started = true;
		this.config = config;
		this.connection.setRegistrated(false);
		this.connection.open(() => {
			this.sendRegisterApplicationRequest();
		});
	}

	public shutdown() {
		this.sendClosePartRequest();
	}

	private sendRegisterApplicationRequest() {
		this.connection.sendMessage(
			EipMessages.registerApplicationRequest(this.config.applicationId, this.storage.getApplicationInstanceId())
		);
	}

	public sendClosePartRequest() {
		if (this.connection.isConnected()) {
			const closePartRequest = EipMessages.closePartRequest(this.connection.getPartInstanceIdentifier());
			this.connection.sendMessage(closePartRequest);
		}
	}

	public onMessage(messageName: string, messageObj: any, id: string) {
		switch (messageName) {
			case 'RegisterApplicationResponse':
				this.onRegisterApplicationResponse(messageObj);
				break;
			case 'ApplicationReadyResponse':
				this.onApplicationReadyResponse(messageObj);
				break;
			case 'LoginRequest':
				this.onLoginRequest(id);
				break;
			case 'GetCourtForOrganizationEntityRequest':
				this.onGetCourtForOrganizationEntityRequest(id);
				break;
			case 'ShowViewRequest':
				this.onShowViewRequest(id, messageObj);
				break;
			case 'AddNewPartResponse':
				this.onAddNewPartResponse(messageObj);
				break;
			case 'GenericErrorResponse':
				this.onGenericErrorResponse(messageObj);
				break;
			default:
				console.log('eIP-Registration: unknown message type: ' + messageName);
		}
	}

	public supportedMessageNames(): string[] {
		return [
			'RegisterApplicationResponse',
			'ApplicationReadyResponse',
			'LoginRequest',
			'GetCourtForOrganizationEntityRequest',
			'ShowViewRequest',
			'AddNewPartResponse',
			'GenericErrorResponse',
		];
	}

	private onRegisterApplicationResponse(messageObj: any) {
		this.errorHandler.assertNoError(messageObj, 'RegisterApplicationResponse');
		this.connection.sendMessage(
			EipMessages.applicationReadyRequest(this.config.applicationId, this.storage.getApplicationInstanceId())
		);
	}

	private onApplicationReadyResponse(messageObj: any) {
		// im Fehlerfall keine Fehlermeldung von eIP-Client
		this.errorHandler.assertNoError(messageObj, 'ApplicationReadyResponse');
		this.eipLawsuit.init(this.config);
		this.eipDigitalFile.init(this.config);
	}

	private onLoginRequest(id: string) {
		const loginResponse: string = EipMessages.loginResponse(this.context.currentUser(), this.config);
		this.connection.sendMessageWithId(id, loginResponse);
	}

	private onGetCourtForOrganizationEntityRequest(id: string) {
		const courtResponse = EipMessages.getCourtForOrganizationEntityResponse(this.config);
		this.connection.sendMessageWithId(id, courtResponse);
	}

	private onShowViewRequest(id: string, messageObj: any) {
		const windowHandle = messageObj.ShowViewRequest.Parameter.Value.toString();

		const viewIdentifier = messageObj.ShowViewRequest.ViewIdentifier.toString();
		if (viewIdentifier !== this.expectedViewIdentifier) {
			this.connection.sendMessage(EipMessages.showViewErrorResponse('unsupported viewIdentifier'));
			this.errorHandler.handle(
				{ Message: 'eIP verlangt falschen ViewIdentifier ' + viewIdentifier },
				'ShowViewResponse'
			);
			return;
		}
		this.connection.sendMessage(EipMessages.addNewPartRequest(windowHandle));
		this.showViewRequestId = id;
	}

	private onAddNewPartResponse(messageObj: any) {
		this.storage.clear();
		this.errorHandler.assertNoError(messageObj, 'AddNewPartResponse');
		this.connection.setRegistrated(true);
		this.connection.setPartInstanceIdentifier(
			messageObj.AddNewPartResponse.Result.PartInstanceIdentifier.toString()
		);
		if (this.showViewRequestId) {
			this.connection.sendMessageWithId(this.showViewRequestId, EipMessages.showViewResponse());
			this.showViewRequestId = null;
		}
	}

	private onGenericErrorResponse(messageObj: any) {
		this.errorHandler.assertNoError(messageObj, 'GenericErrorResponse');
	}
}
